import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { pushNotification } from '@ajat/design-system/Notification/useNotification';
import { fetchOrder, patchOrderItem, cancelOrder } from './ordersApi';
import useBrandScope from '@ajat/design-system/hooks/useBrandScope';

/* ══════════════════════════════════════════════════════════════════════════
 * useEditOrder — shared hook for Edit Order pages (all brands)
 *
 * API-driven: authenticates with the edit token issued at login (or carried in
 * the emailed edit link), loads the real order, and persists edits/cancellation
 * to the backend. The token is kept in sessionStorage so it survives the
 * round-trip to the product page (where the customer edits a line's options).
 *
 * When `apiBrandSlug` is omitted the hook falls back to a static `initialOrder`
 * (used by the design-system preview).
 * ══════════════════════════════════════════════════════════════════════════ */

const EMPTY_ORDER = { orderNumber: '', billing: { name: '', address: '', contact: '' }, products: [], packages: [], studentClub: [] };

/** Map a backend item into the page's product/package shape. */
function mapItem(item) {
  const opts = item.options || {};
  return {
    id: item.id,
    productId: item.productId,
    packageId: item.packageId,
    variationId: item.variationId,
    schoolSlug: item.schoolSlug,
    slug: item.slug,
    name: item.name,
    sku: item.sku,
    image: item.image,
    unitPrice: (Number(item.unitPrice) || 0) + (Number(item.additionalPrice) || 0),
    quantity: item.quantity,
    // Products selected inside a package — shown (with thumbnails) in the cart
    // and the edit-order package card.
    includedProducts: opts.includedProducts || [],
    options: {
      color: opts.color,
      three_d: opts.three_d ?? null,
      size: opts.size,
      attributes: opts.attributes,
      addons: opts.addons,
      image: opts.image,
      // Carry the package's included products through so the edit-order page can
      // render them (name / colour / size + thumbnail) just like the cart.
      includedProducts: opts.includedProducts || [],
      // Human-readable list of every attribute on the line (Color, Size and all
      // dynamic attributes/add-ons), used to render the full option list.
      snapshot: Array.isArray(item.attributes) ? item.attributes : [],
    },
  };
}

/** Map the serialized API order into the page-facing structure. */
function mapOrder(api) {
  if (!api) return EMPTY_ORDER;
  const items = api.items || [];
  return {
    orderNumber: api.orderNumber,
    billing: api.billing || { name: '', address: '', contact: '' },
    school: api.school,
    schoolSlug: api.schoolSlug ?? null,
    shopSlug: api.shopSlug ?? null,
    // Full sales scope (Season / School / Shop / Class) the order was placed in.
    scope: api.scope ?? null,
    totals: api.totals || {},
    editable: api.editable,
    adminMode: api.adminMode === true,
    allowsPriceChanges: api.allowsPriceChanges,
    notEditableReason: api.notEditableReason,
    notEditableReasonCode: api.notEditableReasonCode ?? null,
    products: items.filter((i) => i.type === 'product').map(mapItem),
    packages: items.filter((i) => i.type === 'package').map(mapItem),
    studentClub: [],
  };
}

export default function useEditOrder({
  brand,              // 'ABC' | 'CLS' (for data-brand attr)
  apiBrandSlug,       // backend slug, e.g. 'clsdk' (omit for static preview)
  priceSuffix,        // ':-' | ',-'
  locale,             // 'sv-SE' | 'da-DK'
  momsRate = 0.25,
  initialOrder,       // static fallback for preview mode
  optionLabels,       // { color, size, namn }
  addonKeys,          // [{ dataKey, labelKey }]
  notifications,      // { updated, quantity, removed }
  priceLockedMessage, // localised text shown when a price-changing edit is blocked
}) {
  const location = useLocation();
  const params = useParams();
  const isApi = !!apiBrandSlug;

  // Localise known backend error codes; fall back to the server message.
  const errMessage = (err) =>
    (err?.code === 'price_locked' && priceLockedMessage) || err?.message || '';

  const orderNumber = params.orderNumber || initialOrder?.orderNumber || '';
  const tokenKey = `editOrderToken:${orderNumber}`;
  const adminKey = `editOrderAdmin:${orderNumber}`;

  /* ── Resolve the edit token (login state → URL → sessionStorage) ── */
  const tokenRef = useRef(null);
  if (tokenRef.current === null && isApi) {
    const fromState = location.state?.token;
    const fromUrl = new URLSearchParams(location.search).get('token');
    const fromStore = typeof window !== 'undefined' ? window.sessionStorage.getItem(tokenKey) : null;
    tokenRef.current = fromState || fromUrl || fromStore || '';
    if (tokenRef.current && typeof window !== 'undefined') {
      window.sessionStorage.setItem(tokenKey, tokenRef.current);
    }
  }

  /* ── Admin-edit token (opens the order for an admin even when it's no longer
   *    customer-editable; the backend keeps the paid-card price-lock). Resolved
   *    and persisted exactly like the edit token so it survives the round-trip
   *    to the product/package configurator and back. ── */
  const adminTokenRef = useRef(null);
  if (adminTokenRef.current === null && isApi) {
    const fromState = location.state?.adminEdit;
    const fromUrl = new URLSearchParams(location.search).get('adminEdit');
    const fromStore = typeof window !== 'undefined' ? window.sessionStorage.getItem(adminKey) : null;
    adminTokenRef.current = fromState || fromUrl || fromStore || '';
    if (adminTokenRef.current && typeof window !== 'undefined') {
      window.sessionStorage.setItem(adminKey, adminTokenRef.current);
    }
  }

  const [order, setOrder] = useState(() =>
    isApi ? (location.state?.order ? mapOrder(location.state.order) : EMPTY_ORDER) : initialOrder);
  const [loading, setLoading] = useState(isApi && !location.state?.order);
  const [error, setError] = useState('');
  const currentOrderNumber = order.orderNumber || orderNumber;

  /* ── Load the order from the API ── */
  const loadOrder = useCallback(async () => {
    if (!isApi) return;
    if (!tokenRef.current) {
      setError('expired');
      setLoading(false);
      return;
    }
    try {
      const data = await fetchOrder(apiBrandSlug, orderNumber, tokenRef.current, adminTokenRef.current);
      setOrder(mapOrder(data.order));
      setError('');
    } catch (err) {
      setError(err?.status === 401 ? 'expired' : (err?.message || 'error'));
    } finally {
      setLoading(false);
    }
  }, [apiBrandSlug, orderNumber, isApi]);

  useEffect(() => {
    // Skip the initial fetch only when login already handed us the order AND
    // there's no pending product-page edit to apply.
    if (isApi && (!location.state?.order || location.state?.updatedItem)) {
      loadOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const allowsPriceChanges = order.allowsPriceChanges !== false;
  const editable = order.editable !== false;
  const notEditableReason = order.notEditableReason || null;
  const notEditableReasonCode = order.notEditableReasonCode || null;

  /* ── Totals (stored prices are VAT-inclusive) ── */
  const allItems = [...order.products, ...order.packages, ...order.studentClub];
  const totalInclMoms = order.totals?.total != null
    ? Number(order.totals.total)
    : allItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const totalExMoms = Math.round((totalInclMoms / (1 + momsRate)) * 100) / 100;
  const momsAmount = Math.round((totalInclMoms - totalExMoms) * 100) / 100;

  const formatPrice = useCallback(
    (v) => `${(v || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KR`,
    [locale],
  );

  /* ── Set brand on <html> ── */
  useBrandScope(brand);

  /* ── Apply an item edit coming back from the product page ── */
  useEffect(() => {
    const updated = location.state?.updatedItem;
    if (!updated || !isApi) return;
    window.history.replaceState({}, '');
    (async () => {
      try {
        const data = await patchOrderItem(apiBrandSlug, orderNumber, tokenRef.current, {
          item_id: updated.itemId,
          quantity: updated.quantity,
          options: updated.options,
        }, adminTokenRef.current);
        setOrder(mapOrder(data.order));
        pushNotification({
          type: 'success',
          title: notifications.updated.title,
          message: notifications.updated.message(currentOrderNumber),
        });
      } catch (err) {
        pushNotification({ type: 'error', title: notifications.updated.title, message: errMessage(err) });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  /* ── Debounced quantity PATCH ── */
  const qtyTimers = useRef({});

  const handleQuantityChange = useCallback((section, id, qty) => {
    // Optimistic local update for instant feedback.
    setOrder((prev) => ({
      ...prev,
      [section]: prev[section].map((item) => (item.id === id ? { ...item, quantity: qty } : item)),
    }));

    if (!isApi) return;
    if (qtyTimers.current[id]) clearTimeout(qtyTimers.current[id]);
    qtyTimers.current[id] = setTimeout(async () => {
      try {
        const data = await patchOrderItem(apiBrandSlug, orderNumber, tokenRef.current, { item_id: id, quantity: qty }, adminTokenRef.current);
        setOrder(mapOrder(data.order));
        pushNotification({
          type: 'success',
          title: notifications.quantity.title,
          message: notifications.quantity.message(formatPrice(Number(data.order.totals?.total) || 0)),
        });
      } catch (err) {
        pushNotification({ type: 'error', title: notifications.quantity.title, message: errMessage(err) });
        loadOrder(); // revert to server truth
      }
    }, 700);
  }, [isApi, apiBrandSlug, orderNumber, notifications.quantity, formatPrice, loadOrder]);

  useEffect(() => () => Object.values(qtyTimers.current).forEach(clearTimeout), []);

  /* ── Remove (with confirmation popup) ── */
  const requestRemove = useCallback((section, id, name) => setRemoveTarget({ section, id, name }), []);

  const confirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    const { id, name } = removeTarget;
    setRemoveTarget(null);
    if (!isApi) return;
    try {
      const data = await patchOrderItem(apiBrandSlug, orderNumber, tokenRef.current, { item_id: id, remove: true }, adminTokenRef.current);
      setOrder(mapOrder(data.order));
      pushNotification({
        type: 'success',
        title: notifications.removed.title,
        message: notifications.removed.message(name, formatPrice(Number(data.order.totals?.total) || 0)),
      });
    } catch (err) {
      pushNotification({ type: 'error', title: notifications.removed.title, message: errMessage(err) });
    }
  }, [removeTarget, isApi, apiBrandSlug, orderNumber, notifications.removed, formatPrice]);

  /* ── Cancel order ── */
  const handleCancelConfirm = useCallback(async () => {
    setShowCancelPopup(false);
    if (!isApi) { setCancelled(true); return; }
    try {
      await cancelOrder(apiBrandSlug, orderNumber, tokenRef.current, adminTokenRef.current);
      window.sessionStorage.removeItem(tokenKey);
      setCancelled(true);
    } catch (err) {
      pushNotification({ type: 'error', title: 'Error', message: errMessage(err) });
    }
  }, [isApi, apiBrandSlug, orderNumber, tokenKey]);

  /* ── Build options array for SimpleProduct ── */
  const resolvedAddonKeys = addonKeys ?? [{ dataKey: 'namn', labelKey: 'namn' }];
  const buildOptions = useCallback((opts) => {
    // Prefer the line's attribute snapshot — it lists EVERY attribute with a
    // value (Color, Size + all dynamic attributes/add-ons) with resolved labels.
    const snap = opts?.snapshot;
    if (Array.isArray(snap) && snap.length) {
      return snap
        .filter((s) => s && s.value !== null && s.value !== '' && s.value !== false && s.value !== undefined)
        .map((s) => {
          let label = s.attribute;
          if (s.attribute === 'Color') label = optionLabels.color;
          else if (s.attribute === 'Size') label = optionLabels.size;
          return { label, value: String(s.value) };
        });
    }
    // Legacy fallback (color/size/known add-ons only).
    return [
      opts?.color && { label: optionLabels.color, value: opts.color },
      opts?.size && { label: optionLabels.size, value: opts.size },
      ...resolvedAddonKeys.map(({ dataKey, labelKey }) =>
        opts?.addons?.[dataKey] && { label: optionLabels[labelKey], value: opts.addons[dataKey] }
      ),
    ].filter(Boolean);
  }, [optionLabels, resolvedAddonKeys]);

  return {
    order,
    loading,
    error,
    editable,
    adminMode: order.adminMode === true || !!adminTokenRef.current,
    allowsPriceChanges,
    notEditableReason,
    notEditableReasonCode,
    token: tokenRef.current,
    currentOrderNumber,
    cancelled,
    showCancelPopup,
    setShowCancelPopup,
    removeTarget,
    setRemoveTarget,
    priceSuffix,
    totalExMoms,
    momsAmount,
    totalInclMoms,
    formatPrice,
    handleQuantityChange,
    requestRemove,
    confirmRemove,
    handleCancelConfirm,
    buildOptions,
  };
}
